import { REPORT_MODAL_NAMES } from 'config/labels';
import styles from './EjectionFractionReportModal.module.scss';
import { EFProps } from './EjectionFraction.types';
import { MultiSeriesChart } from '@components/MultiSeriesLineChart/MultiSereisLineChart';
import { useEffect, useRef, useState } from 'react';
import EFReportTable from '@components/ReportTable/EFReportTable';

export default function EjectionFractionModalContent({
  imageSrc,
  values,
}: EFProps) {
  const ef = REPORT_MODAL_NAMES.ef;
  const divRef = useRef<HTMLDivElement | null>(null);
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (!divRef.current) return;
    const currentDiv = divRef.current;
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const { width, height } = entry.contentRect;
        setDimensions({ width, height });
        observer.disconnect();
      });
    });
    observer.observe(currentDiv);
    return () => observer.disconnect();
  }, []);

  return (
     <div className={styles['Report-modal-container']}>
      <div className={styles['content-container']}>
        <div className={styles['content-row']}>
          <div className={styles['container']}>
            <div className={styles['container-img']}>
              <div className={styles['footer-toggle']}>
                  <div className={styles['line']}></div>
                  <span className={styles['text']}>{ef.labels.diastolic}</span>
                </div>
            <div className={styles['image-frame']}>

              <img
                className={styles['image']}
                src={imageSrc?.diastolic}
                alt={ef.labels.diastolic}
              />
            </div>
            </div>
            <div className={styles['container-table']}>
              <div className={styles['footer-toggle']}>
                  <div className={styles['line']}></div>
                  <span className={styles['text']}>{ef.labels.parameter}</span>
                </div>

              <EFReportTable
                columns={ef.Rows}
                rows={[
                  {
                    label: ef.columns[0],
                    values: [
                      values?.lv.ejectionFraction ?? '-',
                      values?.rv.ejectionFraction ?? '-',
                    ],
                  },
                  {
                    label: ef.columns[1],
                    values: [
                      values?.lv.endDiastolicVolume ?? '-',
                      values?.rv.endDiastolicVolume ?? '-',
                    ],
                  },
                  {
                    label: ef.columns[2],
                    values: [
                      values?.lv.endSystolicVolume ?? '-',
                      values?.rv.endSystolicVolume ?? '-',
                    ],
                  },
                  {
                    label: ef.columns[3],
                    values: [
                      values?.lv.strokeVolume ?? '-',
                      values?.rv.strokeVolume ?? '-',
                    ],
                  },
                  {
                    label: ef.columns[4],
                    values: [
                      values?.lv.endDiastolicMyocardialVolume ?? '-',
                      values?.rv.endDiastolicMyocardialVolume ?? '-',
                    ],
                  },
                  {
                    label: ef.columns[5],
                    values: [
                      values?.lv.endDiastolicMyocardialMass ?? '-',
                      values?.rv.endDiastolicMyocardialMass ?? '-',
                    ],
                  },
                ]}
              />
            </div>
          </div>

          <div className={styles['container']}>
            <div className={styles['container-img']}>
              <div className={styles['footer-toggle']}>
                  <div className={styles['line']}></div>
                  <span className={styles['text']}>{ef.labels.systolic}</span>
              </div>
            <div className={styles['image-frame']}>

              <img
                className={styles['image']}
                src={imageSrc?.systolic}
                alt={ef.labels.systolic}
              />
            </div>
            </div>

            <div className={styles['container-chart']}>
              <div className={styles['footer-toggle']}>
                  <div className={styles['line']}></div>
                  <span className={styles['text']}>{ef.labels.cycle}</span>
              </div>

              <div className={styles['content']} ref={divRef}>
                {dimensions.width > 0 && dimensions.height > 0 && (
                  <MultiSeriesChart
                    xLabel="%"
                    yLabel="Volume (mL)"
                    width={dimensions.width}
                    height={dimensions.height}
                    series={[
                      {
                        label: 'Left Ventricle',
                        color: '#1C7ED6',
                        data: values?.lv.data ?? [],
                      },
                      {
                        label: 'Right Ventricle',
                        color: '#F03E3E',
                        data: values?.rv.data ?? [],
                      },
                    ]}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
     </div>
  );
}
